import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '~/lib/utils'

export interface DrawerProps {
    open: boolean
    onClose: () => void
    title?: ReactNode
    children: ReactNode
    /** 패널 폭(기본 max-w-md). */
    className?: string
}

/**
 * 우측 슬라이드오버(드로어) — 배경(backdrop) 클릭/ESC/X로 닫힘, 우측 고정, 내부 스크롤.
 * 중앙 정렬이 필요하면 Modal을 쓴다.
 */
export function Drawer({
    open,
    onClose,
    title,
    children,
    className,
}: DrawerProps) {
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])

    if (!open) return null
    return (
        <div
            className='fixed inset-0 z-50 flex justify-end bg-black/40'
            onClick={onClose}>
            <div
                className={cn(
                    'border-border bg-card flex h-full w-full max-w-md flex-col border-l shadow-xl',
                    className
                )}
                onClick={(e) => e.stopPropagation()}
                role='dialog'
                aria-modal='true'>
                {title != null ? (
                    <div className='border-border flex items-center justify-between border-b px-5 py-3'>
                        <h2 className='text-foreground text-base font-semibold'>
                            {title}
                        </h2>
                        <button
                            type='button'
                            onClick={onClose}
                            className='text-muted-foreground hover:text-foreground'
                            aria-label='닫기'>
                            <X className='size-4' />
                        </button>
                    </div>
                ) : null}
                <div className='min-h-0 flex-1 overflow-y-auto p-5'>
                    {children}
                </div>
            </div>
        </div>
    )
}
